import React, { useEffect, useState } from 'react';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import Form from '../../../../../components/form/Form';
import Panel from '../../../../../components/Panel';
import Select from '../../../../../components/input/Select';
import {
  BRAND_WIDGET_LAYOUT_TYPE_LABEL_V2,
  WIDGET_TYPE_LABEL_V2,
  WIDGET_TYPE_SELECT_LIST_V2,
} from '../../../../../constants/service/app/widgetConstantsV2';
import {
  WIDGET_TYPE_V2,
  BrandWidgetDetailV2,
  BrandWidgetRequestPayloadV2,
  BRAND_WIDGET_LAYOUT_TYPE_V2,
  WIDGET_LAYOUT_V2,
} from '../../../../../types/widget';
import WidgetLayoutSelect from '../../common/components/WidgetLayoutSelect';
import Notice from '../../../../../components/Notice';
import Input from '../../../../../components/input/Input/Input';
import TextArea from '../../../../../components/input/TextArea/TextArea';
import { InfiniteRangePicker } from '../../../../../components/input/RangePicker';
import { DATE_FORMAT } from '../../../../../types/common/date';
import SelectList from '../../../../../components/input/SelectList';
import { BRAND_UPLOAD_TEMPLATE_XLSX_URL } from '../../../../../constants/service/common/staticFileUrlConstants';
import {
  EXPOSURE_WIDGET_CONTENTS_MAX_COUNT,
  EXPOSURE_WIDGET_MAX_COUNT,
  useBrandWidgetFormRules,
} from './useBrandWidgetFormRules';
import { brandColumns } from '../../common/columns';
import { brandListFetcher } from '../../common/fetchers/brandListFetcher';
import { useBrandListExcel } from '../../../../../hooks/brand/useBrandListExcel';
import FormListTab, { TabItem } from '../../../../../components/input/FormListTab/FormListTab';
import { asyncSimpleModal, getRandomNumber } from '../../../../../lib/common';
import ImageAndVideoUploadV2 from '../../../../../components/input/ImageAndVideoUploadV2';
import { BrandItemV2 } from '../../../../../types/brand/list';
import FormWithTab from '../../../../../components/form/Form/FormWithTab';
import { useBrandWidgetForm } from './useBrandWidgetForm';
import { WIDGET } from '../../../../../constants/queryKeys';
import { FieldError } from '../../../../../types/common/form';
import WidgetFormFooter from '../../WidgetFormFooter';
import BrandWidgetPreviewModal from './BrandWidgetPreviewModal';
import Warning from '../../../../../components/Warning';
import toastMsg from '../../../../../lib/toastMsg';
import { textNormalize } from '../../../../../lib/utils/form/normalize';

const getItemCountWarningMessage = (type?: BRAND_WIDGET_LAYOUT_TYPE_V2): string => {
  switch (type) {
    case BRAND_WIDGET_LAYOUT_TYPE_V2.GRID_1X4:
    case BRAND_WIDGET_LAYOUT_TYPE_V2.GRID_2X2:
      return `위젯 아이템을 ${EXPOSURE_WIDGET_MAX_COUNT[BRAND_WIDGET_LAYOUT_TYPE_V2.GRID_1X4]}개 선택해주세요.`;
    case BRAND_WIDGET_LAYOUT_TYPE_V2.SCROLL_NX1:
    case BRAND_WIDGET_LAYOUT_TYPE_V2.SCROLL_BLANK_NX1:
      return '위젯 아이템을 최소 3개 선택해주세요.';
    case BRAND_WIDGET_LAYOUT_TYPE_V2.SWIPE:
      return '위젯 아이템을 최소 2개 선택해주세요.';
    default:
      return `위젯 아이템을 ${EXPOSURE_WIDGET_MAX_COUNT[BRAND_WIDGET_LAYOUT_TYPE_V2.GRID_1X4]}개 선택해주세요.`;
  }
};

interface BrandWidgetFormProps {
  isEdit: boolean;
  form: WrappedFormUtils;
  initialValue: Partial<BrandWidgetDetailV2>;
  onChangeType: (type: WIDGET_TYPE_V2) => void;
  onFinish: (value: BrandWidgetRequestPayloadV2) => void;
}

const BrandWidgetForm = (props: BrandWidgetFormProps): JSX.Element => {
  const { isEdit, initialValue, form, onFinish, onChangeType } = props;
  const [currentTab, setCurrentTab] = useState<number>();
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  useEffect(() => {
    if (!currentTab && initialValue.exposureWidgetItemBrands && initialValue.exposureWidgetItemBrands.length)
      setCurrentTab(initialValue.exposureWidgetItemBrands[0].id);
  }, [initialValue]);

  const { layoutType, exposureWidgetItemBrands, handleChangeBrandList, handleChangeExposureWidgetItemBrands } =
    useBrandWidgetForm({
      form,
      initialValue,
      changeTab: (tab) => setCurrentTab(tab),
    });

  const formRule = useBrandWidgetFormRules({ form, initialValue });

  const { uploadBrandListExcel, downloadBrandListExcel } = useBrandListExcel({
    onUploadSuccess: async (res) => {
      const newList = res.brands.map((brand) => ({
        id: getRandomNumber(),
        brand: new BrandItemV2(brand),
        title: brand.name,
      }));

      if (layoutType && newList.length > EXPOSURE_WIDGET_MAX_COUNT[layoutType]) {
        await asyncSimpleModal({
          type: 'error',
          title: '아이템 개수 초과',
          message: `아이템 개수는 최대 ${EXPOSURE_WIDGET_MAX_COUNT[layoutType]}개 입니다.`,
        });
        return;
      }

      form.setFieldsValue({
        widgetBrandList: res.brands.map((brand) => new BrandItemV2(brand)),
        exposureWidgetItemBrands: newList,
      });

      if (newList.length) setCurrentTab(newList[0].id);

      toastMsg.success('위젯 아이템 EXCEL 업로드하기 성공');
    },
  });

  const handleFinish = (value: BrandWidgetDetailV2): void => onFinish(new BrandWidgetRequestPayloadV2(value));

  const handleFinishFailed = (errors: FieldError[]): void => {
    const tabError = errors.find((err) => err.field === 'exposureWidgetItemBrands');
    if (tabError && tabError.errors && tabError.errors.length > 0) {
      const firstErrorTab = tabError.errors[0].id;
      if (firstErrorTab) setCurrentTab(firstErrorTab);
    }
  };

  const validateLayoutSelect = (layoutType: WIDGET_LAYOUT_V2): string | null => {
    if (exposureWidgetItemBrands && exposureWidgetItemBrands.length > EXPOSURE_WIDGET_MAX_COUNT[layoutType]) {
      return `현재 선택된 위젯 브랜드 아이템 개수는 ${exposureWidgetItemBrands.length}개 입니다. 변경하실 위젯 브랜드(${
        BRAND_WIDGET_LAYOUT_TYPE_LABEL_V2[layoutType as BRAND_WIDGET_LAYOUT_TYPE_V2]
      }) 아이템 개수는 최대 ${EXPOSURE_WIDGET_MAX_COUNT[layoutType]}개 입니다. 브랜드 아이템 개수를 ${
        EXPOSURE_WIDGET_MAX_COUNT[layoutType]
      }개 이하로 설정하시고 변경하십시오.`;
    }
    return null;
  };

  return (
    <FormWithTab
      form={form}
      name="exposureWidgetItemBrands"
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
      initialValue={initialValue}
    >
      <Panel title="위젯 정보">
        <Panel.Section>
          <Form.Item
            name="type"
            label="위젯 타입"
            notice={isEdit ? <Notice>* 위젯 복사, 편집 시에는 위젯 데이터 타입을 변경할 수 없습니다.</Notice> : null}
          >
            {isEdit ? (
              <span>{WIDGET_TYPE_LABEL_V2[WIDGET_TYPE_V2.BRAND]}</span>
            ) : (
              <Select list={WIDGET_TYPE_SELECT_LIST_V2} onChange={onChangeType} style={{ width: 140 }} />
            )}
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item name="layoutType" label="위젯 레이아웃 타입">
            <WidgetLayoutSelect type={WIDGET_TYPE_V2.BRAND} checkValidSelect={validateLayoutSelect} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="mainTitle"
            label="위젯 타이틀"
            rules={formRule.mainTitle.rules}
            normalize={textNormalize}
            notice={<Notice>* 위젯 타이틀의 글자 수는 최대 20자입니다.</Notice>}
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="subTitle"
            label="위젯 서브 타이틀"
            rules={formRule.subTitle.rules}
            normalize={textNormalize}
            notice={<Notice>* 위젯 서브 타이틀의 글자 수는 최대 20자입니다.</Notice>}
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item name="memo" label="메모" maxCount={1000} rules={formRule.memo.rules} normalize={textNormalize}>
            <TextArea />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="exposurePeriod"
            label="위젯 노출기간"
            notice={<Notice>* 해당 위젯이 메인/서브 페이지에서 노출되는 기간입니다.</Notice>}
          >
            <InfiniteRangePicker format={DATE_FORMAT.FULL_DASH_FORMAT} showTime={true} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item label="노출되고 있는 페이지">
            <span>{initialValue?.currentExposurePage || '-'}</span>
          </Form.Item>
        </Panel.Section>
      </Panel>
      <Panel title="위젯 상세">
        <Panel.Section>
          <Form.Item
            name="widgetBrandList"
            layoutType="vertical"
            rules={formRule.widgetBrandList.rules}
            notice={
              <>
                <Warning>{getItemCountWarningMessage(layoutType)}</Warning>
                <Notice>* 선택된 아이템은 회색으로 보이게 되며, 선택이 되지 않습니다.</Notice>
              </>
            }
          >
            <SelectList
              max={formRule.brandList.max}
              canDrag={true}
              onChange={handleChangeBrandList}
              excelTemplateUrl={BRAND_UPLOAD_TEMPLATE_XLSX_URL}
              queryKey={WIDGET.BRAND_LIST}
              fetcher={brandListFetcher}
              columns={brandColumns}
              initialOption={{ pagination: { pageNum: 0, pageSize: 20 } }}
              onExcelUpload={uploadBrandListExcel}
              onDownloadExcel={downloadBrandListExcel}
            />
          </Form.Item>
        </Panel.Section>
      </Panel>
      <Panel title="홈에서 브랜드 위젯 노출 정보를 결정하는 옵션">
        <Panel.Section>
          <Form.Item name="exposureWidgetItemBrands">
            <FormListTab
              notice={<Notice>드래그 앤 드랍으로 위젯 섹션의 순서를 변경할 수 있습니다.</Notice>}
              onChange={handleChangeExposureWidgetItemBrands}
              onChangeActiveKey={setCurrentTab}
              activeKey={currentTab}
              canAdd={false}
              items={(value: BrandWidgetDetailV2['exposureWidgetItemBrands']): TabItem[] =>
                value.map((item) => ({
                  id: item.id,
                  title: item.brand.name,
                  content: () => (
                    <>
                      <Panel.Section hideIf={formRule.exposureWidgetItemBrands.innerRules.attachment.hideIf}>
                        <Form.Item
                          name="attachment"
                          label="이미지"
                          required={true}
                          rules={formRule.exposureWidgetItemBrands.innerRules.attachment.rules}
                          notice={
                            <Notice>
                              <p>* 각 브랜드별 홈에 노출 될 이미지입니다.</p>
                              <p>* 브랜드별 이미지의 높이와 너비는 모두 일정하게 업로드하시기 바랍니다.</p>
                            </Notice>
                          }
                        >
                          <ImageAndVideoUploadV2 modalTitle="메인 이미지" />
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section>
                        <Form.Item
                          name="title"
                          label="위젯 브랜드 타이틀"
                          rules={formRule.exposureWidgetItemBrands.innerRules.title.rules}
                          normalize={textNormalize}
                          notice={
                            layoutType !== BRAND_WIDGET_LAYOUT_TYPE_V2.GRID_1X4 ? (
                              <Notice>* 위젯 상점 아이템 타이틀의 글자 수는 최대 20자입니다.</Notice>
                            ) : null
                          }
                        >
                          {layoutType === BRAND_WIDGET_LAYOUT_TYPE_V2.GRID_1X4 ? (
                            <span>{item.brand.name}</span>
                          ) : (
                            <Input />
                          )}
                        </Form.Item>
                      </Panel.Section>
                      <Panel.Section hideIf={formRule.exposureWidgetItemBrands.innerRules.contents.hideIf}>
                        <Form.Item
                          name="contents"
                          label="위젯 브랜드 내용"
                          rules={formRule.exposureWidgetItemBrands.innerRules.contents.rules}
                          normalize={textNormalize}
                          notice={
                            layoutType && (
                              <Notice>{`* 위젯 상점 아이템 내용의 글자 수는 최대 ${EXPOSURE_WIDGET_CONTENTS_MAX_COUNT[layoutType]}자입니다.`}</Notice>
                            )
                          }
                        >
                          <Input />
                        </Form.Item>
                      </Panel.Section>
                    </>
                  ),
                }))
              }
            />
          </Form.Item>
        </Panel.Section>
      </Panel>
      <Form.Item>
        <WidgetFormFooter
          typeLabel={WIDGET_TYPE_LABEL_V2[WIDGET_TYPE_V2.BRAND]}
          onClickPreview={(): void => setIsPreviewModalOpen(true)}
        />
      </Form.Item>
      <BrandWidgetPreviewModal
        isOpen={isPreviewModalOpen}
        detail={form.getFieldsValue() as BrandWidgetDetailV2}
        onClose={(): void => setIsPreviewModalOpen(false)}
      />
    </FormWithTab>
  );
};

export default Form.create<BrandWidgetFormProps>()(BrandWidgetForm);
